// Phase 4 — Financial Reports page (17 reports with PDF + Excel export)
import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, FileDown, FileText, Play, BarChart3, Printer, BookOpen, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

function fmtMoney(v) {
  // Phase 24 (2026-07-13) — global bracket-notation accounting format.
  // Mirrors backend `format_amount_accounting` so on-screen and PDF/Excel
  // presentations of negatives are identical.
  const n = Number(v || 0);
  const abs = Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  if (n < 0) return `($${abs})`;
  return `$${abs}`;
}
function todayISO() { return new Date().toISOString().slice(0, 10); }
function yearStartISO() {
  return `${new Date().getFullYear()}-01-01`;
}
function lastYearStartISO() {
  return `${new Date().getFullYear() - 1}-01-01`;
}
function monthStartISO() {
  const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function quarterStartISO() {
  const d = new Date(); const q = Math.floor(d.getMonth() / 3) * 3;
  return `${d.getFullYear()}-${String(q + 1).padStart(2, "0")}-01`;
}

function MoneyCell({ v }) { return <span className="tabular-nums">{fmtMoney(v)}</span>; }

function QuickRange({ label, onClick, active, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`text-xs rounded-full px-3 py-1 border transition-colors ${
        active
          ? "bg-[#4FA3D1] text-white border-[#4FA3D1]"
          : "bg-white text-slate-600 border-slate-200 hover:border-[#4FA3D1] hover:text-[#4FA3D1]"
      }`}>{label}</button>
  );
}

function EmptyState({ hint }) {
  return (
    <div className="text-center py-16 px-4">
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400 mb-3">
        <BarChart3 className="h-6 w-6"/>
      </div>
      <p className="text-slate-600 font-medium">No data for the selected period.</p>
      <p className="text-xs text-slate-400 mt-1">{hint || "Try a wider date range — e.g. \"Year to Date\" or \"All Time\" — or post some transactions first."}</p>
    </div>
  );
}

function tableSection(title, headers, rows, footer) {
  return (
    <div className="space-y-2">
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              {headers.map((h, i) => (
                <TableHead key={i} className={h.right ? "text-right" : ""}>{h.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0
              ? <TableRow><TableCell colSpan={headers.length} className="text-center text-slate-400 py-6">No data.</TableCell></TableRow>
              : rows.map((r, i) => (
                <TableRow key={i}>
                  {r.map((cell, j) => (
                    <TableCell key={j} className={headers[j]?.right ? "text-right tabular-nums" : ""}>{cell}</TableCell>
                  ))}
                </TableRow>
              ))}
            {footer && (
              <TableRow className="font-bold border-t-2 border-slate-300 bg-slate-50">
                {footer.map((cell, j) => (
                  <TableCell key={j} className={headers[j]?.right ? "text-right tabular-nums" : ""}>{cell}</TableCell>
                ))}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// Phase 52-E (2026-08-06) — group report rows under their parent so a
// hierarchical rollup renders on Balance Sheet / P&L.
function groupRowsWithParents(rows) {
  const byCode = new Map(rows.map((r) => [r.code, r]));
  const parentToChildren = new Map();
  for (const r of rows) {
    if (r.parent_code && byCode.has(r.parent_code)) {
      const arr = parentToChildren.get(r.parent_code) || [];
      arr.push(r); parentToChildren.set(r.parent_code, arr);
    }
  }
  const orderedTop = rows
    .filter((r) => !r.parent_code || !byCode.has(r.parent_code))
    .sort((a, b) => String(a.code).localeCompare(String(b.code)));
  const out = [];
  for (const p of orderedTop) {
    const kids = (parentToChildren.get(p.code) || []).slice().sort(
      (a, b) => String(a.code).localeCompare(String(b.code)));
    if (kids.length === 0) {
      out.push({ row: p, indent: 0, isRollupParent: false });
      continue;
    }
    const kidTotal = kids.reduce((s, k) => s + Number(k.amount || 0), 0);
    const rollup = Number(p.amount || 0) + kidTotal;
    out.push({ row: p, indent: 0, isRollupParent: true, rollupTotal: rollup });
    for (const k of kids) out.push({ row: k, indent: 1, isRollupParent: false });
  }
  return out;
}

function renderReport(reportId, data) {
  if (!data) return <EmptyState />;
  // Defensive accessor — many reports use empty arrays for "no data";
  // protect against any missing keys causing a `.map of undefined` crash.
  const arr = (v) => (Array.isArray(v) ? v : []);
  // Small helper: renders a report section with parent/child rollup rows.
  const groupedSection = (title, rows, totalKey, amountLabel = "Balance") => {
    const grouped = groupRowsWithParents(rows);
    return tableSection(
      title,
      [{ label: "Code" }, { label: "Account" }, { label: amountLabel, right: true }],
      grouped.map((g) => {
        const r = g.row;
        const codeCell = g.indent === 0
          ? (g.isRollupParent
              ? <span className="font-semibold text-slate-900">{r.code}</span>
              : r.code)
          : <span className="pl-5 text-slate-500">↳ {r.code}</span>;
        const nameCell = g.indent === 0
          ? (g.isRollupParent ? <span className="font-semibold">{r.name}</span> : r.name)
          : <span className="pl-5 text-slate-700">{r.name}</span>;
        const amountCell = g.isRollupParent
          ? <span className="font-semibold"><MoneyCell v={g.rollupTotal} /></span>
          : <MoneyCell v={r.amount} />;
        return [codeCell, nameCell, amountCell];
      }),
      ["", `Total ${title}`, <MoneyCell v={data[totalKey]} />],
    );
  };
  if (reportId === "trial_balance") {
    const rows = arr(data.rows);
    if (rows.length === 0) return <EmptyState />;
    // Phase 52-E (2026-08-06) — Trial Balance stays flat but now shows a
    // Parent column so the user can spot the rollup families at a glance.
    return tableSection(
      "",
      [{ label: "Code" }, { label: "Account" }, { label: "Parent" },
       { label: "Debit", right: true }, { label: "Credit", right: true }],
      rows.map((r) => [
        r.parent_code
          ? <span className="pl-4 text-slate-500">↳ {r.code}</span>
          : r.code,
        r.parent_code ? <span className="pl-4 text-slate-700">{r.name}</span> : r.name,
        r.parent_code
          ? <span className="font-mono text-xs text-slate-500">{r.parent_code}</span>
          : <span className="text-slate-300">—</span>,
        <MoneyCell v={r.debit} />, <MoneyCell v={r.credit} />,
      ]),
      ["", "Totals", "", <MoneyCell v={data.total_debit} />, <MoneyCell v={data.total_credit} />],
    );
  }
  if (reportId === "income_statement") {
    const inc = arr(data.income), exp = arr(data.expenses);
    if (inc.length === 0 && exp.length === 0) return <EmptyState />;
    return (
      <div className="space-y-6">
        {groupedSection("Income", inc, "total_income", "Amount")}
        {groupedSection("Expenses", exp, "total_expenses", "Amount")}
        <Card className="border-l-4 border-l-[#4FA3D1]">
          <CardContent className="p-5">
            <p className="text-xs uppercase tracking-wider text-slate-500">Net Profit / (Loss)</p>
            <p className="mt-2 text-3xl font-bold text-slate-900">{fmtMoney(data.net_profit)}</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (reportId === "balance_sheet") {
    const a = arr(data.assets), l = arr(data.liabilities), e = arr(data.equity);
    if (a.length === 0 && l.length === 0 && e.length === 0) return <EmptyState />;
    return (
      <div className="space-y-6">
        {groupedSection("Assets", a, "total_assets")}
        {groupedSection("Liabilities", l, "total_liabilities")}
        {groupedSection("Equity", e, "total_equity")}
        <p className={`text-sm ${data.balanced ? "text-emerald-700" : "text-red-700 font-semibold"}`}>
          Assets = Liabilities + Equity → {data.balanced ? "Balanced ✓" : "NOT balanced — investigate"}
        </p>
      </div>
    );
  }
  if (reportId === "ap_aging" || reportId === "ar_aging") {
    const parties = arr(data.parties);
    if (parties.length === 0) return <EmptyState hint="No outstanding aging balances right now." />;
    const t = data.totals || {};
    return tableSection(
      "",
      [{ label: "Party" }, { label: "0–30", right: true }, { label: "31–60", right: true },
       { label: "61–90", right: true }, { label: "90+", right: true }, { label: "Total", right: true }],
      parties.map((p) => [p.party,
        <MoneyCell v={p["0-30"]} />, <MoneyCell v={p["31-60"]} />,
        <MoneyCell v={p["61-90"]} />, <MoneyCell v={p["90+"]} />,
        <span className="font-semibold tabular-nums">{fmtMoney(p.total)}</span>]),
      ["Grand Total", <MoneyCell v={t["0-30"]} />, <MoneyCell v={t["31-60"]} />,
       <MoneyCell v={t["61-90"]} />, <MoneyCell v={t["90+"]} />, <MoneyCell v={t.total} />],
    );
  }
  if (reportId === "cash_flow") {
    // Phase 33 Item 5 (2026-07-17) — GAAP subcategory presentation.
    const gaap = data.gaap || {};
    const op = gaap.operating || { lines: [], net: 0, label: "Cash flows from operating activities" };
    const inv = gaap.investing || { lines: [], net: 0, label: "Cash flows from investing activities" };
    const fin = gaap.financing || { lines: [], net: 0, label: "Cash flows from financing activities" };
    const itSummary = data.internal_transfer_summary || { count: 0, gross_movement: 0 };
    const renderSubSection = (title, sec) => {
      const lines = sec.lines || [];
      const rows = lines.length === 0
        ? [[<span className="text-slate-500 italic">— No activity —</span>, ""]]
        : lines.map((ln) => [
            ln.label,
            <a
              href={`/transactions?je_ids=${(ln.source_ids || []).join(",")}&from=cash_flow`}
              className="tabular-nums font-medium text-[#4FA3D1] hover:underline"
              data-testid={`cash-flow-sub-${ln.key}`}
            >{fmtMoney(ln.amount)}</a>,
          ]);
      const netLabel = title.toLowerCase().replace("cash flows from ", "");
      return tableSection(
        title,
        [{ label: "Description" }, { label: "Amount", right: true }],
        rows,
        [<strong>{`Net cash from (used in) ${netLabel}`}</strong>,
         <strong className="tabular-nums">{fmtMoney(sec.net)}</strong>],
      );
    };
    return (
      <div className="space-y-4" data-testid="cash-flow-report">
        <Card><CardContent className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          <div><p className="text-xs uppercase text-slate-500">Opening</p><p className="text-lg font-bold">{fmtMoney(data.opening_balance)}</p></div>
          <div><p className="text-xs uppercase text-slate-500">Operating</p><p className="text-lg font-bold text-emerald-700">{fmtMoney(op.net)}</p></div>
          <div><p className="text-xs uppercase text-slate-500">Investing</p><p className="text-lg font-bold text-sky-700">{fmtMoney(inv.net)}</p></div>
          <div><p className="text-xs uppercase text-slate-500">Financing</p><p className="text-lg font-bold text-amber-700">{fmtMoney(fin.net)}</p></div>
          <div><p className="text-xs uppercase text-slate-500">Closing</p><p className="text-lg font-bold" data-testid="cash-flow-closing">{fmtMoney(data.closing_balance)}</p>
            {data.reconciles ? <span className="text-[10px] text-emerald-600">✓ reconciles with GL</span>
              : <span className="text-[10px] text-red-600" data-testid="cash-flow-drift">⚠ drift vs GL {fmtMoney(data.gl_cash_bank_balance)}</span>}
          </div>
        </CardContent></Card>
        {renderSubSection(op.label || "Cash flows from operating activities", op)}
        {renderSubSection(inv.label || "Cash flows from investing activities", inv)}
        {renderSubSection(fin.label || "Cash flows from financing activities", fin)}
        <Card><CardContent className="p-4 space-y-1">
          <div className="flex justify-between text-base">
            <span className="font-semibold">Net increase / (decrease) in cash</span>
            <span className="tabular-nums font-bold">{fmtMoney(data.net_change_in_cash)}</span>
          </div>
          <div className="flex justify-between text-base border-t pt-1">
            <span className="font-semibold">Closing Cash &amp; Bank Balance</span>
            <span className="tabular-nums font-bold">{fmtMoney(data.closing_balance)}</span>
          </div>
        </CardContent></Card>
        {itSummary.count > 0 && (
          <p className="text-xs text-slate-500 italic" data-testid="cash-flow-internal-transfers-note">
            Internal cash &amp; bank transfers during the period: <strong>{itSummary.count}</strong> transactions,
            gross movement <strong>{fmtMoney(itSummary.gross_movement)}</strong>. These net to zero and do NOT affect the net cash position.
          </p>
        )}
      </div>
    );
  }
  if (reportId === "petty_cash") {
    const rows = arr(data.rows);
    if (rows.length === 0) return <EmptyState />;
    return (
      <div className="space-y-3">
        {tableSection("",
          [{ label: "Date" }, { label: "Narration" }, { label: "Ref" },
           { label: "Debit", right: true }, { label: "Credit", right: true }, { label: "Balance", right: true }],
          rows.map((r) => [r.date, r.narration, r.reference,
            <MoneyCell v={r.debit} />, <MoneyCell v={r.credit} />, <MoneyCell v={r.balance} />]))}
        <p className="text-sm">Closing Balance: <strong>{fmtMoney(data.closing_balance)}</strong></p>
      </div>
    );
  }
  if (reportId === "bank_reconciliation") {
    // Phase 32 Item 4 (2026-07-17) — Per-bank-account 10-line breakdown.
    const secs = arr(data.sections);
    if (secs.length === 0) return <EmptyState hint="No bank accounts found in the CoA." />;
    return (
      <div className="space-y-5" data-testid="bank-recon-report">
        {secs.map((s) => (
          <Card key={s.account_id} data-testid={`bank-recon-section-${s.account_code}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">{s.account_code} — {s.account_name}</p>
                  <p className="text-xs text-slate-500">Reconciliation for {data.period_from} → {data.period_to}</p>
                </div>
                {s.is_matched
                  ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Matched</Badge>
                  : <Badge className="bg-red-100 text-red-700 border-red-200" data-testid={`bank-recon-drift-${s.account_code}`}>Diff {fmtMoney(s.reconciliation_difference)}</Badge>}
              </div>
              <table className="w-full text-sm">
                <tbody className="divide-y">
                  <tr><td className="py-1.5">Opening book balance</td><td className="text-right tabular-nums">{fmtMoney(s.opening_balance)}</td></tr>
                  <tr><td className="py-1.5">+ Deposits &amp; receipts</td><td className="text-right tabular-nums text-emerald-700">{fmtMoney(s.deposits)}</td></tr>
                  <tr><td className="py-1.5">+ Internal transfers in</td><td className="text-right tabular-nums text-slate-500">{fmtMoney(s.internal_transfers_in)}</td></tr>
                  <tr><td className="py-1.5">− Payments &amp; withdrawals</td><td className="text-right tabular-nums text-red-700">({fmtMoney(s.payments)})</td></tr>
                  <tr><td className="py-1.5">− Internal transfers out</td><td className="text-right tabular-nums text-slate-500">({fmtMoney(s.internal_transfers_out)})</td></tr>
                  <tr><td className="py-1.5">− Bank charges</td><td className="text-right tabular-nums text-red-700">({fmtMoney(s.bank_charges)})</td></tr>
                  <tr className="font-semibold bg-slate-50"><td className="py-1.5">Closing book balance</td><td className="text-right tabular-nums">{fmtMoney(s.closing_book_balance)}</td></tr>
                  <tr><td className="py-1.5">Statement closing balance</td><td className="text-right tabular-nums">{fmtMoney(s.statement_closing_balance)}</td></tr>
                  <tr><td className="py-1.5">Adjustments</td><td className="text-right tabular-nums">{fmtMoney(s.adjustments)}</td></tr>
                  <tr className="font-bold border-t-2 border-slate-300"><td className="py-1.5">Reconciliation difference</td>
                    <td className={`text-right tabular-nums ${Math.abs(s.reconciliation_difference) < 0.02 ? "text-emerald-700" : "text-red-700"}`}>{fmtMoney(s.reconciliation_difference)}</td></tr>
                </tbody>
              </table>
              {s.source_ids?.length > 0 && (
                <div className="pt-2 text-xs text-slate-500">
                  <a href={`/bookkeeping?account=${s.account_code}&from=dashboard`} className="text-[#4FA3D1] hover:underline" data-testid={`bank-recon-drill-${s.account_code}`}>
                    View {s.source_ids.length} underlying JE{s.source_ids.length === 1 ? "" : "s"} →
                  </a>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }
  if (reportId === "payroll_summary") {
    const runs = arr(data.runs);
    const totals = data.totals || {};
    // Phase 49 (2026-07-29) - column set: Period, Paid Date, Status, Emp.,
    // Gross, PAYE, Net, PAYE Cleared?  (Employer Tax + SS dropped.)
    // The Show-voided/archived toggle is owned by ReportsPage (below) and
    // simply refreshes with `?include_voided_archived=true`.
    const paidCell = (r) => {
      // Phase 49 rule: a posted run is by definition NOT YET paid, even
      // if paid_date is set on the row (defensive against stale data
      // written by earlier migration revisions). Status check wins over
      // the paid_date presence.
      if (r.status === "posted") {
        return <span className="text-xs text-slate-400 italic">Not yet paid</span>;
      }
      if (r.paid_date) return <span className="tabular-nums">{r.paid_date}</span>;
      return <span className="text-slate-300">—</span>;
    };
    const statusPill = (r) => {
      const cls = {
        posted: "bg-blue-100 text-blue-700",
        paid:   "bg-emerald-100 text-emerald-700",
        voided: "bg-red-100 text-red-700",
      }[r.status] || "bg-slate-100 text-slate-700";
      return (
        <span className="inline-flex items-center gap-1">
          <Badge className={cls}>{r.status}</Badge>
          {r.archived && <Badge className="bg-slate-200 text-slate-500">archived</Badge>}
        </span>
      );
    };
    const paycCell = (r) => {
      const p = r.paye_cleared || {};
      const state = p.state || "not_filed";
      if (state === "cleared") {
        const dt = (p.paid_at || "").slice(0, 10);
        return (
          <Badge className="bg-emerald-100 text-emerald-700"
                 data-testid="paye-cleared-pill-cleared">
            Cleared{dt ? ` - ${dt}` : ""}
          </Badge>
        );
      }
      if (state === "outstanding") {
        return (
          <Badge className="bg-amber-100 text-amber-800"
                 data-testid="paye-cleared-pill-outstanding">
            Outstanding - {fmtMoney(p.amount || 0)}
          </Badge>
        );
      }
      if (state === "overdue") {
        return (
          <Badge className="bg-red-100 text-red-700"
                 data-testid="paye-cleared-pill-overdue">
            Overdue by {p.days_overdue || 0}d
          </Badge>
        );
      }
      return (
        <Badge className="bg-slate-100 text-slate-500"
               data-testid="paye-cleared-pill-not-filed">
          Not filed
        </Badge>
      );
    };
    return (
      <div className="space-y-6" data-testid="payroll-summary-report">
        {runs.length === 0 ? (
          <EmptyState hint="No payroll runs match the current filter." />
        ) : (
          tableSection("Runs",
            [{ label: "Period" }, { label: "Paid Date" }, { label: "Status" },
             { label: "Emp.", right: true },
             { label: "Gross", right: true }, { label: "PAYE", right: true },
             { label: "Net", right: true }, { label: "PAYE Cleared?" }],
            runs.map((r) => [r.period, paidCell(r), statusPill(r), r.employees,
              <span className="font-semibold tabular-nums">{fmtMoney(r.gross)}</span>,
              <MoneyCell v={r.paye} />,
              <span className="font-semibold tabular-nums">{fmtMoney(r.net)}</span>,
              paycCell(r)]),
            ["", "", "Totals", "",
             <span className="font-semibold tabular-nums">{fmtMoney(totals.gross)}</span>,
             <MoneyCell v={totals.paye} />,
             <span className="font-semibold tabular-nums">{fmtMoney(totals.net)}</span>,
             ""])
        )}
        {arr(data.employees).length > 0 && tableSection("Per Employee",
          [{ label: "Period" }, { label: "Paid Date" }, { label: "Employee" },
           { label: "Gross", right: true }, { label: "PAYE", right: true },
           { label: "Net", right: true }],
          arr(data.employees)
            .slice()
            .sort((a, b) => {
              if ((b.period || "") !== (a.period || "")) {
                return (b.period || "").localeCompare(a.period || "");
              }
              return (a.employee || "").localeCompare(b.employee || "");
            })
            .map((e) => [e.period, e.paid_date || "—", e.employee,
              <span className="font-semibold tabular-nums">{fmtMoney(e.gross)}</span>,
              <MoneyCell v={e.paye} />,
              <span className="font-semibold tabular-nums">{fmtMoney(e.net)}</span>]))}
      </div>
    );
  }
  if (reportId === "payroll_instruction") {
    const lines = arr(data.lines);
    const obligations = arr(data.obligations);
    return (
      <div className="space-y-6" data-testid="payroll-instruction-report">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="rounded-md border border-slate-200 p-3">
            <p className="text-[11px] uppercase text-slate-500">Payroll Month</p>
            <p className="text-sm font-semibold" data-testid="pi-month">{data.payroll_month_label || "—"}</p>
          </div>
          <div className="rounded-md border border-slate-200 p-3">
            <p className="text-[11px] uppercase text-slate-500">Payable Date</p>
            <p className="text-sm font-semibold" data-testid="pi-payable-date">{data.payable_date_label || data.payable_date || "—"}</p>
          </div>
          <div className="rounded-md border border-slate-200 p-3">
            <p className="text-[11px] uppercase text-slate-500">Headcount</p>
            <p className="text-sm font-semibold" data-testid="pi-headcount">{data.headcount ?? 0}</p>
          </div>
          <div className="rounded-md border border-slate-200 p-3">
            <p className="text-[11px] uppercase text-slate-500">Total Cost</p>
            <p className="text-sm font-semibold tabular-nums" data-testid="pi-total-cost">{fmtMoney(data.total_cost)}</p>
          </div>
          <div className="rounded-md border border-slate-200 p-3">
            <p className="text-[11px] uppercase text-slate-500">Method</p>
            <p className="text-sm font-semibold">{data.payment_method || "Bank transfer"}</p>
          </div>
        </div>
        {lines.length === 0
          ? <EmptyState hint="No payroll run for this month yet." />
          : tableSection("Employee Payment Instruction",
              [{ label: "#" }, { label: "Employee" }, { label: "Position" }, { label: "Bank" },
               { label: "Net Salary", right: true }, { label: "Allowances", right: true },
               { label: "Deductions", right: true }, { label: "PAYE", right: true }],
              lines.map((l, i) => [
                i + 1,
                <div>{l.employee_name}<div className="text-xs text-slate-500">{l.department}</div></div>,
                l.position || "—",
                l.bank_missing
                  ? <Badge className="bg-red-100 text-red-700 border-red-200" data-testid={`bank-missing-${l.employee_id}`}>Bank details missing</Badge>
                  : <div className="text-xs"><div className="font-medium">{l.bank_name}</div><div className="text-slate-500">{l.account_number}</div></div>,
                <span className="font-semibold tabular-nums">{fmtMoney(l.gross_salary)}</span>,
                <MoneyCell v={l.allowances} />,
                <MoneyCell v={l.deductions} />, <MoneyCell v={l.paye_withheld} />,
              ]),
              ["", `Totals (${data.headcount} employees)`, "", "",
               <span className="font-semibold tabular-nums">{fmtMoney(data.total_gross)}</span>,
               <MoneyCell v={data.total_allowances} />,
               <MoneyCell v={data.total_deductions} />, <MoneyCell v={data.total_paye} />])
        }
        {obligations.length > 0 && tableSection("Statutory Obligations",
          [{ label: "Obligation" }, { label: "Authority" },
           { label: "Amount", right: true }, { label: "Due Date" },
           { label: "GL Account" }, { label: "Reference" }],
          obligations.map((o) => [
            o.name, o.authority, <MoneyCell v={o.amount} />,
            o.due_date, o.gl_account, o.reference,
          ]))
        }
      </div>
    );
  }
  if (reportId === "tax_clearance" || reportId === "compliance_followup") {
    const rows = arr(data.rows);
    if (rows.length === 0) return <EmptyState hint="No compliance items in this period." />;
    return tableSection("",
      [{ label: "Title" }, { label: "Category" }, { label: "Authority" }, { label: "Ref" },
       { label: "Due" }, { label: "Status" }, { label: "Days Left", right: true }],
      rows.map((r) => [r.title, <Badge variant="secondary">{r.category}</Badge>, r.authority,
        r.reference_no, r.due_date, <Badge>{r.status}</Badge>,
        r.days_remaining === null ? "—" : (r.days_remaining < 0
          ? <span className="text-red-700 font-semibold">{r.days_remaining}</span>
          : r.days_remaining)]));
  }
  if (reportId === "payroll_tax") {
    const items = arr(data.items);
    return (
      <div className="space-y-4">
        <Card className="border-l-4 border-l-[#E63946]"><CardContent className="p-5">
          <p className="text-xs uppercase text-slate-500">Payroll Tax Payable (acct 2100)</p>
          <p className="mt-2 text-3xl font-bold text-slate-900">{fmtMoney(data.payroll_tax_payable)}</p>
        </CardContent></Card>
        {items.length === 0
          ? <Card><CardContent className="p-6 text-sm text-slate-500 text-center">No filing items for the selected period.</CardContent></Card>
          : tableSection("Compliance items",
            [{ label: "Title" }, { label: "Authority" }, { label: "Due" }, { label: "Status" }],
            items.map((r) => [r.title, r.authority, r.due_date, <Badge>{r.status}</Badge>]))}
      </div>
    );
  }
  if (reportId === "missing_documents") {
    const rows = arr(data.rows);
    if (rows.length === 0) return <EmptyState hint="No missing documents — every posted transaction has at least one attachment." />;
    return tableSection("",
      [{ label: "Date" }, { label: "Type" }, { label: "Amount", right: true },
       { label: "Status" }, { label: "Reference" }, { label: "Created By" }],
      rows.map((r) => [r.date, r.type, <MoneyCell v={r.amount} />,
        <Badge variant="secondary">{r.status}</Badge>, r.reference, r.created_by]));
  }
  if (reportId === "internal_controls") {
    const summary = arr(data.summary);
    const ub = arr(data.unbalanced_je);
    const od = arr(data.old_drafts);
    return (
      <div className="space-y-4">
        {summary.length > 0 && tableSection("Control Summary",
          [{ label: "Control" }, { label: "Issues", right: true }],
          summary.map((s) => [s.control,
            (s.issues || 0) === 0
              ? <Badge className="bg-emerald-100 text-emerald-700">{s.issues || 0}</Badge>
              : <Badge className="bg-red-100 text-red-700">{s.issues}</Badge>]))}
        {ub.length > 0 && (
          <Card><CardContent className="p-4">
            <h4 className="font-semibold text-red-700">Unbalanced Journal Entries</h4>
            <ul className="text-sm mt-2 list-disc pl-5">
              {ub.map((j) => <li key={j.id}>{j.entry_date} — {j.narration}: Dr {fmtMoney(j.debit)} ≠ Cr {fmtMoney(j.credit)}</li>)}
            </ul>
          </CardContent></Card>
        )}
        {od.length > 0 && (
          <Card><CardContent className="p-4">
            <h4 className="font-semibold text-amber-700">Old Draft Transactions</h4>
            <ul className="text-sm mt-2 list-disc pl-5">
              {od.map((d) => <li key={d.id || d.reference}>{d.date} — {d.reference} ({d.type}): {fmtMoney(d.amount)}</li>)}
            </ul>
          </CardContent></Card>
        )}
        {summary.length === 0 && ub.length === 0 && od.length === 0 && <EmptyState hint="No internal control exceptions for this period. 🎉" />}
      </div>
    );
  }
  if (reportId === "submitted_status") {
    const rows = arr(data.rows);
    if (rows.length === 0) return <EmptyState hint="No submitted transactions in this period." />;
    return tableSection("",
      [{ label: "Status" }, { label: "Count", right: true }, { label: "Total Amount", right: true }],
      rows.map((r) => [<Badge variant="secondary">{r.status}</Badge>, r.count, <MoneyCell v={r.total_amount} />]),
      ["Grand Total", data.grand_count, <MoneyCell v={data.grand_total} />]);
  }
  if (reportId === "vat_summary") {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500">Net Sales</p><p className="text-xl font-bold tabular-nums">{fmtMoney(data.total_net)}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500">Output VAT</p><p className="text-xl font-bold tabular-nums text-emerald-700">{fmtMoney(data.total_vat)}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500">VAT-bearing invoices</p><p className="text-xl font-bold tabular-nums">{data.invoice_count || 0}</p></CardContent></Card>
        </div>
        {tableSection("By Period",
          [{ label: "Period" }, { label: "Invoices", right: true }, { label: "Net", right: true },
           { label: "VAT", right: true }, { label: "Status" }],
          arr(data.rows).map((r) => [r.period, r.count,
            <MoneyCell v={r.net} />, <MoneyCell v={r.vat} />, <Badge variant="secondary">{r.status}</Badge>]),
          ["Totals", "", <MoneyCell v={data.total_net} />, <MoneyCell v={data.total_vat} />, ""])}
      </div>
    );
  }
  if (reportId === "wht_summary") {
    const byCat = arr(data.by_category), bySup = arr(data.by_supplier), txs = arr(data.transactions);
    return (
      <div className="space-y-4">
        <Card><CardContent className="p-4">
          <p className="text-xs uppercase text-slate-500">Total Withheld</p>
          <p className="text-3xl font-bold tabular-nums">{fmtMoney(byCat.reduce((s, c) => s + (c.total || 0), 0))}</p>
        </CardContent></Card>
        <div className="grid lg:grid-cols-2 gap-3">
          {tableSection("By Category",
            [{ label: "Category" }, { label: "Count", right: true }, { label: "Total", right: true }],
            byCat.map((c) => [c.category, c.count, <MoneyCell v={c.total} />]))}
          {tableSection("By Supplier",
            [{ label: "Supplier" }, { label: "Count", right: true }, { label: "Total", right: true }],
            bySup.map((s) => [s.supplier, s.count, <MoneyCell v={s.total} />]))}
        </div>
        {tableSection("Transactions",
          [{ label: "Date" }, { label: "Reference" }, { label: "Supplier" },
           { label: "Category" }, { label: "Gross", right: true }, { label: "WHT", right: true }],
          txs.map((t) => [t.transaction_date, t.reference_number, t.counterparty_name,
            <Badge variant="secondary">{t.wht_category}</Badge>,
            <MoneyCell v={t.amount} />, <MoneyCell v={t.wht_amount} />]))}
      </div>
    );
  }
  if (reportId === "corporate_tax") {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500">Sales / Year {data.year}</p><p className="text-xl font-bold tabular-nums">{fmtMoney(data.sales)}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500">Net Profit</p><p className={`text-xl font-bold tabular-nums ${Number(data.net_profit) < 0 ? "text-rose-700" : "text-slate-900"}`}>{fmtMoney(data.net_profit)}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500">CIT Liability</p><p className="text-xl font-bold tabular-nums text-rose-700">{fmtMoney(data.liability)}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500">Paid YTD</p><p className="text-xl font-bold tabular-nums text-emerald-700">{fmtMoney(data.paid_ytd)}</p></CardContent></Card>
        </div>
        {data.tier_applied && (
          <Card className="border-l-4 border-l-violet-500"><CardContent className="p-4">
            <p className="text-xs uppercase text-slate-500">Tier Applied</p>
            <p className="text-sm mt-1">
              <span className="font-semibold capitalize">{String(data.tier_applied.type).replace("_", " ")}</span>
              {" @ "}<span className="font-mono">{data.tier_applied.value}</span>
              {" — sales range "}{fmtMoney(data.tier_applied.sales_min)}–{data.tier_applied.sales_max ? fmtMoney(data.tier_applied.sales_max) : "∞"}
            </p>
          </CardContent></Card>
        )}
        {data.quarterly && tableSection("Quarterly Schedule",
          [{ label: "Quarter" }, { label: "Due" }, { label: "Amount", right: true }],
          arr(data.quarterly).map((q) => [`Q${q.q}`, q.due_date, <MoneyCell v={q.amount} />]))}
        {data.payments && arr(data.payments).length > 0 && tableSection("Payments YTD",
          [{ label: "Date" }, { label: "Reference" }, { label: "Amount", right: true }],
          arr(data.payments).map((p) => [p.payment_date, p.reference || "—", <MoneyCell v={p.amount} />]))}
      </div>
    );
  }
  if (reportId === "loan_summary") {
    const t = data.totals || {};
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500">Loans</p><p className="text-xl font-bold tabular-nums">{t.count || 0}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500">Principal (Total)</p><p className="text-xl font-bold tabular-nums">{fmtMoney(t.principal)}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500">Outstanding</p><p className="text-xl font-bold tabular-nums text-rose-700">{fmtMoney(t.outstanding)}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500">Interest Paid</p><p className="text-xl font-bold tabular-nums text-emerald-700">{fmtMoney(t.interest_paid_total)}</p></CardContent></Card>
        </div>
        {tableSection("Loans",
          [{ label: "Loan No." }, { label: "Lender" }, { label: "Start" }, { label: "Rate" },
           { label: "Principal", right: true }, { label: "Outstanding", right: true },
           { label: "Paid in Period", right: true }, { label: "Status" }, { label: "Next Due" }],
          arr(data.rows).map((r) => [r.loan_number, `${r.lender} (${r.lender_type})`,
            r.start_date, `${r.rate}%`,
            <MoneyCell v={r.principal} />, <MoneyCell v={r.outstanding} />,
            <MoneyCell v={r.paid_in_period} />,
            <Badge variant="secondary">{r.status}</Badge>,
            r.next_payment_date ? `${r.next_payment_date} · ${fmtMoney(r.next_payment_amount)}` : "—"]),
          ["Totals", "", "", "",
           <MoneyCell v={t.principal} />, <MoneyCell v={t.outstanding} />,
           <MoneyCell v={t.paid_in_period} />, "", ""])}
      </div>
    );
  }
  // Generic fallback — try to render any { rows: [...] } shape as a table,
  // else show a clean empty state.
  if (data && Array.isArray(data.rows) && data.rows.length > 0) {
    const cols = Object.keys(data.rows[0]);
    return tableSection("",
      cols.map((c) => ({ label: c.replace(/_/g, " ") })),
      data.rows.map((r) => cols.map((c) => {
        const v = r[c];
        if (typeof v === "number") return <MoneyCell v={v} />;
        if (typeof v === "object" && v !== null) return JSON.stringify(v);
        return v === null || v === undefined ? "—" : String(v);
      })));
  }
  return <EmptyState />;
}

function SafeRenderReport({ reportId, data }) {
  // Defensive wrapper — a malformed payload should never blank the report card.
  try {
    return renderReport(reportId, data);
  } catch (err) {
    return (
      <div className="space-y-3">
        <div className="rounded-md border border-rose-200 bg-rose-50 text-rose-800 p-3 text-sm">
          <p className="font-semibold">Could not render this report.</p>
          <p className="text-xs mt-1">{String(err?.message || err)}</p>
        </div>
        <p className="text-xs text-slate-500">Raw payload below — please report this to support.</p>
        <pre className="text-xs bg-slate-50 border border-slate-200 rounded-md p-3 overflow-x-auto max-h-[400px]">
          {JSON.stringify(data, null, 2)}
        </pre>
      </div>
    );
  }
}

export default function ReportsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [list, setList] = useState([]);
  // Phase 48 (2026-07-29) - allow the Dashboard's Total Income card (and any
  // other link) to deep-link into a specific report via ?report=<id>.
  const initialSelected = searchParams.get("report") || "trial_balance";
  const [selected, setSelected] = useState(initialSelected);
  // Default: Year-to-date — far more useful than "last 30 days" for finance reports.
  const [start, setStart] = useState(yearStartISO());
  const [end, setEnd] = useState(todayISO());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null); // "pdf" | "xlsx" | null
  // P1 (2026-07-12) — Income-Statement print-options modal.
  const [printOpts, setPrintOpts] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem("st_print_opts") || "{}");
      return { mode: "internal", show_account_codes: true,
                show_zero_accounts: false, show_signature_block: true,
                show_notes_section: true, comparison_mode: "none",
                format: "pdf", ...cached };
    } catch { return { mode: "internal", show_account_codes: true,
                        show_zero_accounts: false, show_signature_block: true,
                        show_notes_section: true, comparison_mode: "none",
                        format: "pdf" }; }
  });
  const [modalOpen, setModalOpen] = useState(false);
  // Phase 49 (2026-07-29) - Payroll Summary voided/archived visibility toggle.
  const [includeVoidedArchived, setIncludeVoidedArchived] = useState(false);
  // Bug bundle #5 (2026-08-10) - User Guide PDF download progress.
  const [downloadingGuide, setDownloadingGuide] = useState(false);

  // Phase 24 (2026-07-13) — applicability sets. Mirror backend
  // `SIGNATURE_APPLICABLE` / `COMPARISON_APPLICABLE`. Used to hide toggles
  // that don't apply to a given report.
  const SIGNATURE_APPLICABLE = new Set([
    "trial_balance", "income_statement", "balance_sheet", "cash_flow",
    "bank_reconciliation", "petty_cash",
    "ap_aging", "ar_aging",
    "payroll_summary", "payroll_instruction",
    "tax_clearance", "vat_summary", "wht_summary", "corporate_tax",
    "loan_summary",
  ]);
  const COMPARISON_APPLICABLE = new Set([
    "income_statement", "balance_sheet", "cash_flow", "trial_balance",
  ]);

  useEffect(() => {
    api.get("/reports").then(({ data }) => setList(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  // Phase 48 - keep `selected` in sync when the URL changes externally
  // (back/forward nav, deep links) AND persist the current selection back
  // into the URL so refresh / share-link works.
  useEffect(() => {
    const fromUrl = searchParams.get("report");
    if (fromUrl && fromUrl !== selected) setSelected(fromUrl);
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get("report") !== selected) {
      const next = new URLSearchParams(searchParams);
      next.set("report", selected);
      setSearchParams(next, { replace: true });
    }
  }, [selected]);

  const loadReport = useCallback(async () => {
    setLoading(true); setReport(null);
    try {
      // Phase 49 - forward the include_voided_archived flag for the
      // Payroll Summary. Ignored by every other report.
      const params = { period_start: start, period_end: end };
      if (selected === "payroll_summary" && includeVoidedArchived) {
        params.include_voided_archived = true;
      }
      const { data } = await api.get(`/reports/${selected}`, { params });
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [selected, start, end, includeVoidedArchived]);

  useEffect(() => { loadReport(); /* eslint-disable-next-line */ }, [selected, includeVoidedArchived]);

  async function exportFile(format, extraOpts = {}) {
    setExporting(format);
    try {
      const token = localStorage.getItem("st_token");
      // Phase 49 - propagate include_voided_archived to export endpoints
      // when running the Payroll Summary export.
      const merged = { period_start: start, period_end: end, ...extraOpts };
      if (selected === "payroll_summary" && includeVoidedArchived) {
        merged.include_voided_archived = "true";
      }
      const params = new URLSearchParams(merged);
      const url = `${API}/reports/${selected}/${format}?${params.toString()}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status}: ${txt}`);
      }
      const blob = await res.blob();
      const ext = format === "pdf" ? "pdf" : "xlsx";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${selected}_${start}_${end}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally { setExporting(null); }
  }

  const supportsComparison = COMPARISON_APPLICABLE.has(selected);
  const supportsSignature = SIGNATURE_APPLICABLE.has(selected);

  function openPrintModal() {
    setModalOpen(true);
  }

  async function confirmPrint() {
    localStorage.setItem("st_print_opts", JSON.stringify(printOpts));
    setModalOpen(false);
    const { format, ...rest } = printOpts;
    // Silently drop comparison_mode for reports that don't support it.
    if (!supportsComparison) rest.comparison_mode = "none";
    if (!supportsSignature) rest.show_signature_block = false;
    await exportFile(format, rest);
  }

  // Bug bundle #5 (2026-08-10) — download the auto-generated 30-page User Guide.
  // Bug bundle #6 (2026-08-11) — supports both PDF and DOCX formats.
  async function downloadUserGuide(format = "pdf") {
    setDownloadingGuide(true);
    try {
      const token = localStorage.getItem("st_token");
      const ext = format === "docx" ? "docx" : "pdf";
      const res = await fetch(`${API}/reports/user-guide.${ext}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status}: ${txt}`);
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `somturk_user_guide.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`User Guide (${ext.toUpperCase()}) downloaded`);
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally {
      setDownloadingGuide(false);
    }
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[280px,1fr]" data-testid="reports-page">
      {/* Sidebar */}
      <Card className="lg:sticky lg:top-4 lg:self-start max-h-[calc(100vh-2rem)] overflow-hidden">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 mb-2 px-2 pt-1">
            <BarChart3 className="h-4 w-4 text-[#4FA3D1]" />
            <h3 className="text-sm font-semibold text-slate-700">Reports</h3>
          </div>
          <nav className="space-y-1 overflow-y-auto max-h-[60vh]" data-testid="reports-nav">
            {list.map((r) => (
              <button key={r.id} data-testid={`report-${r.id}`}
                onClick={() => setSelected(r.id)}
                className={`w-full text-left text-sm rounded-md px-3 py-2 transition-colors ${
                  selected === r.id
                    ? "bg-[#4FA3D1]/10 text-[#0B1929] font-semibold"
                    : "text-slate-600 hover:bg-slate-100"}`}>
                {r.title}
              </button>
            ))}
          </nav>
        </CardContent>
      </Card>

      {/* Main preview */}
      <div className="space-y-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex-1">
                <h2 className="text-xl font-bold text-slate-900">
                  {list.find((r) => r.id === selected)?.title || ""}
                </h2>
                {report && <p className="text-xs text-slate-500 mt-1">
                  Generated {report.generated_at?.slice(0, 19).replace("T", " ")} by {user?.email}
                </p>}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                <div>
                  <Label className="text-xs">From</Label>
                  <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start" />
                </div>
                <div>
                  <Label className="text-xs">To</Label>
                  <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end" />
                </div>
                <Button onClick={loadReport} className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" data-testid="generate-report-btn">
                  <Play className="h-4 w-4 mr-1.5" /> Generate
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3 items-center">
              <span className="text-xs text-slate-500 mr-1">Quick range:</span>
              <QuickRange label="This Month" onClick={() => { setStart(monthStartISO()); setEnd(todayISO()); }} active={start === monthStartISO() && end === todayISO()} testId="qr-month"/>
              <QuickRange label="This Quarter" onClick={() => { setStart(quarterStartISO()); setEnd(todayISO()); }} active={start === quarterStartISO() && end === todayISO()} testId="qr-quarter"/>
              <QuickRange label="Year to Date" onClick={() => { setStart(yearStartISO()); setEnd(todayISO()); }} active={start === yearStartISO() && end === todayISO()} testId="qr-ytd"/>
              <QuickRange label="Last 12 Months" onClick={() => { setStart(lastYearStartISO()); setEnd(todayISO()); }} active={start === lastYearStartISO() && end === todayISO()} testId="qr-12m"/>
              <QuickRange label="All Time" onClick={() => { setStart("2020-01-01"); setEnd("2030-12-31"); }} active={start === "2020-01-01" && end === "2030-12-31"} testId="qr-all"/>
              <span className="flex-1"/>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" disabled={downloadingGuide}
                          data-testid="download-user-guide-btn"
                          title="Download the SOM-TURK Finance System User Guide">
                    {downloadingGuide
                      ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                      : <BookOpen className="h-4 w-4 mr-1.5" />}
                    User Guide
                    <ChevronDown className="h-3.5 w-3.5 ml-1 opacity-70" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  <DropdownMenuItem
                    data-testid="download-user-guide-pdf"
                    onClick={() => downloadUserGuide("pdf")}
                    className="cursor-pointer">
                    <FileText className="h-4 w-4 mr-2 text-rose-600" />
                    Download PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="download-user-guide-docx"
                    onClick={() => downloadUserGuide("docx")}
                    className="cursor-pointer">
                    <FileText className="h-4 w-4 mr-2 text-blue-600" />
                    Download DOCX
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="outline" size="sm" disabled={!report || exporting}
                       onClick={openPrintModal}
                       data-testid="export-pdf-btn">
                {exporting === "pdf" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Printer className="h-4 w-4 mr-1.5" />}
                Print Options
              </Button>
              <Button variant="outline" size="sm" disabled={!report || exporting} onClick={() => exportFile("xlsx")} data-testid="export-xlsx-btn">
                {exporting === "xlsx" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" />}
                Export Excel
              </Button>
            </div>
            {/* Phase 49 (2026-07-29) - Payroll Summary voided/archived toggle. */}
            {selected === "payroll_summary" && (
              <div className="mt-3 flex items-center gap-2 text-xs text-slate-600 border-t border-slate-100 pt-3">
                <input
                  type="checkbox"
                  id="report-payroll-show-voided"
                  checked={includeVoidedArchived}
                  onChange={(e) => setIncludeVoidedArchived(e.target.checked)}
                  data-testid="report-payroll-show-voided"
                />
                <label htmlFor="report-payroll-show-voided" className="cursor-pointer select-none">
                  Show voided / archived runs
                </label>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#4FA3D1]" /></div>
            ) : !report ? (
              <p className="text-slate-400 text-center py-16">Select a report and date range.</p>
            ) : (
              <div data-testid="report-preview">
                {renderReport(selected, report.data)}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      {/* Phase 24 (2026-07-13) — Print Options modal now applies to ALL 21 reports. */}
      <Dialog open={modalOpen} onOpenChange={(o) => !o && setModalOpen(false)}>
        <DialogContent className="max-w-lg" data-testid="print-options-modal">
          <DialogHeader><DialogTitle>Print Options — {list.find((r) => r.id === selected)?.title || "Report"}</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Report type</Label>
              <RadioGroup className="flex gap-6 mt-2" value={printOpts.mode}
                          onValueChange={(v) => setPrintOpts((o) => ({ ...o, mode: v }))}>
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="rt-int" value="internal" data-testid="mode-internal" />
                  <Label htmlFor="rt-int">Internal</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="rt-ext" value="external" data-testid="mode-external" />
                  <Label htmlFor="rt-ext">External</Label>
                </div>
              </RadioGroup>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {[
                ["show_account_codes", "Show account codes", true],
                ["show_zero_accounts", "Show zero-activity accounts", true],
                ["show_notes_section", "Show notes section", true],
                ["show_signature_block", "Show signature block", supportsSignature],
              ].filter(([, , applies]) => applies).map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={!!printOpts[k]}
                            data-testid={`chk-${k}`}
                            onCheckedChange={(v) => setPrintOpts((o) => ({ ...o, [k]: !!v }))} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            {supportsComparison && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Comparison</Label>
              <Select value={printOpts.comparison_mode}
                      onValueChange={(v) => setPrintOpts((o) => ({ ...o, comparison_mode: v }))}>
                <SelectTrigger data-testid="comparison-select" className="w-full mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="previous_period">Previous Period</SelectItem>
                  <SelectItem value="budget">Budget</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Format</Label>
              <RadioGroup className="flex gap-6 mt-2" value={printOpts.format}
                          onValueChange={(v) => setPrintOpts((o) => ({ ...o, format: v }))}>
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="fmt-pdf" value="pdf" data-testid="fmt-pdf" />
                  <Label htmlFor="fmt-pdf">PDF</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="fmt-xlsx" value="xlsx" data-testid="fmt-xlsx" />
                  <Label htmlFor="fmt-xlsx">Excel</Label>
                </div>
              </RadioGroup>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} data-testid="print-cancel">Cancel</Button>
            <Button className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white"
                    onClick={confirmPrint} disabled={!!exporting}
                    data-testid="print-confirm">
              {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />}
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
